import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'

import {HOVERCARD_KEYBOARD_SHORTCUT, isHovercardHintEnabledForUser, isHovercardEnabledForUser} from './preferences'

/**
 * Base options common to all hovercard types.
 */
export type BaseHovercardOptions = {
  /** Include octo-click/octo-dimensions tracking attributes. Default: true */
  tracking?: boolean
}

/**
 * Hovercard attributes without tracking.
 */
export type HovercardAttributes = {
  'data-hovercard-url': string
  'data-hovercard-type': string
}

/**
 * Hovercard attributes with analytics tracking.
 */
export type HovercardAttributesWithTracking = HovercardAttributes & {
  'octo-click': string
  'octo-dimensions': string
}

/**
 * Result type for hovercard attribute functions.
 * Returns empty object when hovercard cannot be generated (missing params, disabled, server render).
 */
export type HovercardAttributesResult = HovercardAttributes | HovercardAttributesWithTracking | Record<string, never>

// Stable empty result for server/disabled cases - avoids creating new objects
const EMPTY_RESULT: HovercardAttributesResult = Object.freeze({})

/**
 * Add keyboard hint aria attribute if enabled for the user.
 */
export function addKeyboardHint<T extends Record<string, string>>(attributes: T): T & {'aria-keyshortcuts'?: string} {
  if (isHovercardHintEnabledForUser()) {
    return {...attributes, 'aria-keyshortcuts': HOVERCARD_KEYBOARD_SHORTCUT}
  }
  return attributes
}

/**
 * Add analytics tracking attributes to hovercard attributes.
 */
export function addTrackingAttributes(attributes: HovercardAttributes): HovercardAttributesWithTracking {
  return addKeyboardHint({
    ...attributes,
    'octo-click': 'hovercard-link-click',
    'octo-dimensions': 'link_type:self',
  })
}

/**
 * Build final hovercard attributes with optional tracking.
 */
export function buildHovercardAttributes(
  url: string,
  type: string,
  tracking: boolean = true,
): HovercardAttributesResult {
  const attributes: HovercardAttributes = {
    'data-hovercard-url': url,
    'data-hovercard-type': type,
  }

  if (tracking) {
    return addTrackingAttributes(attributes)
  }
  return addKeyboardHint(attributes)
}

/**
 * Hook wrapper that handles server-side rendering and user preferences.
 * Returns EMPTY_RESULT on server or when hovercards are disabled.
 */
export function useHovercardAttributesBase(attributes: HovercardAttributesResult): HovercardAttributesResult {
  const {isServer} = useRuntimeEnvironment()

  if (isServer || !isHovercardEnabledForUser()) {
    return EMPTY_RESULT
  }

  return attributes
}

/**
 * Get an empty hovercard result. Use when conditions aren't met.
 */
export function emptyHovercardResult(): HovercardAttributesResult {
  return EMPTY_RESULT
}
